import {gql} from '@apollo/client';

// Every news-service pipeline operation returns a union with Error. Lists
// arrive under `data`, with the platform's `pagination` beside them.

const SOURCE_FIELDS = gql`
  fragment NewsSourceFields on RegisteredNewsSource {
    id
    name
    homepageUrl
    host
    enabled
    defaultLanguage
    coverage {
      exchangeId
      exchangeCode
      exchangeName
      enabled
      sectionFilter
      gnewsLocale
      keywords
      trustWeight
      defaultTag
      defaultType
      defaultCategories
      requireKeywords
      autoPublish
    }
    discovery {
      mode
      resolvedMode
      feedUrl
      sitemapUrl
      listUrl
      resolvedAt
    }
    extraction {
      mode
      selectors {
        title
        content
        image
        date
        author
      }
    }
    fetch {
      intervalMinutes
      maxItemsPerRun
      userAgent
      respectRobots
    }
    health {
      nextRunAt
      lastRunAt
      lastSuccessAt
      consecutiveFailures
      lastError
      itemsLast7d
    }
    createdAt
    updatedAt
  }
`;

export const GET_NEWS_SOURCES = gql`
  ${SOURCE_FIELDS}
  query NewsSources(
    $exchangeId: ID
    $enabled: Boolean
    $search: String
    $limit: Int
    $page: Int
  ) {
    newsSources(
      exchangeId: $exchangeId
      enabled: $enabled
      search: $search
      limit: $limit
      page: $page
    ) {
      ... on Error {
        message
      }
      ... on RegisteredNewsSourceList {
        data {
          ...NewsSourceFields
        }
      }
    }
  }
`;

// The website picker on Story activity and Review: names only, searched by
// news-service as the admin types.
export const GET_WEBSITE_OPTIONS = gql`
  query NewsWebsiteOptions($exchangeId: ID, $search: String, $limit: Int) {
    newsSources(exchangeId: $exchangeId, search: $search, limit: $limit) {
      ... on RegisteredNewsSourceList {
        data {
          id
          name
        }
      }
    }
  }
`;

export const GET_NEWS_SOURCE = gql`
  ${SOURCE_FIELDS}
  query NewsSource($sourceId: ID!) {
    newsSource(sourceId: $sourceId) {
      ... on Error {
        message
      }
      ... on RegisteredNewsSource {
        ...NewsSourceFields
      }
    }
  }
`;

export const GET_NEWS_SOURCE_EXCHANGE_HEALTH = gql`
  query NewsSourceExchangeHealth($exchangeId: ID!) {
    newsSourceExchangeHealth(exchangeId: $exchangeId) {
      ... on Error {
        message
      }
      ... on NewsSourceExchangeHealth {
        exchangeId
        total
        enabled
        healthy
        itemsLast7d
        unrouted
      }
    }
  }
`;

export const PROBE_NEWS_SOURCE = gql`
  query ProbeNewsSource(
    $homepageUrl: String!
    $gnewsLocale: String
    $feedUrl: String
  ) {
    probeNewsSource(
      homepageUrl: $homepageUrl
      gnewsLocale: $gnewsLocale
      feedUrl: $feedUrl
    ) {
      ... on Error {
        message
      }
      ... on NewsSourceProbeResult {
        host
        alreadyRegistered
        existingSourceId
        previouslyDeleted
        resolvedMode
        feedUrl
        sitemapUrl
        attempts {
          rung
          url
          status
          itemCount
        }
        warnings
        sampleArticles {
          title
          link
          publishedDate
          hasFullContent
          excerpt
        }
      }
    }
  }
`;

export const GET_UNROUTED_ARTICLES = gql`
  query UnroutedArticles($limit: Int) {
    unroutedArticles(limit: $limit) {
      ... on Error {
        message
      }
      ... on IngestedArticleList {
        data {
          id
          sourceId
          sourceName
          sourceHost
          canonicalUrl
          title
          excerpt
          language
          publishedDate
        }
      }
    }
  }
`;

export const CREATE_NEWS_SOURCE = gql`
  ${SOURCE_FIELDS}
  mutation CreateNewsSource($data: CreateNewsSourceInput!) {
    createNewsSource(data: $data) {
      ... on Error {
        message
      }
      ... on RegisteredNewsSource {
        ...NewsSourceFields
      }
    }
  }
`;

export const UPDATE_NEWS_SOURCE = gql`
  ${SOURCE_FIELDS}
  mutation UpdateNewsSource($sourceId: ID!, $data: UpdateNewsSourceInput!) {
    updateNewsSource(sourceId: $sourceId, data: $data) {
      ... on Error {
        message
      }
      ... on RegisteredNewsSource {
        ...NewsSourceFields
      }
    }
  }
`;

export const DELETE_NEWS_SOURCE = gql`
  mutation DeleteNewsSource($sourceId: ID!) {
    deleteNewsSource(sourceId: $sourceId) {
      ... on Error {
        message
      }
      ... on DeletedNewsSource {
        status
      }
    }
  }
`;

export const ATTACH_NEWS_SOURCE_COVERAGE = gql`
  mutation AttachNewsSourceCoverage(
    $sourceId: ID!
    $coverage: NewsSourceCoverageInput!
  ) {
    attachNewsSourceCoverage(sourceId: $sourceId, coverage: $coverage) {
      ... on Error {
        message
      }
      ... on RegisteredNewsSource {
        id
        name
      }
    }
  }
`;

export const DETACH_NEWS_SOURCE_COVERAGE = gql`
  mutation DetachNewsSourceCoverage($sourceId: ID!, $exchangeId: ID!) {
    detachNewsSourceCoverage(sourceId: $sourceId, exchangeId: $exchangeId) {
      ... on Error {
        message
      }
      ... on RegisteredNewsSource {
        id
        name
      }
    }
  }
`;

export const RETRY_NEWS_SOURCE = gql`
  mutation RetryNewsSource($sourceId: ID!) {
    retryNewsSource(sourceId: $sourceId) {
      ... on Error {
        message
      }
      ... on NewsSourceRetryResult {
        status
        resolvedMode
        ingested
        skipped
        unrouted
        error
      }
    }
  }
`;

export const ASSIGN_ARTICLE_EXCHANGE = gql`
  mutation AssignArticleExchange(
    $articleId: ID!
    $exchangeId: ID!
    $learnKeyword: String
  ) {
    assignArticleExchange(
      articleId: $articleId
      exchangeId: $exchangeId
      learnKeyword: $learnKeyword
    ) {
      ... on Error {
        message
      }
      ... on IngestedArticle {
        id
        status
      }
    }
  }
`;

// "Not stock market news" in Articles we couldn't place is hidden until
// news-service adds rejectIngestedArticle(articleId): IngestedArticleResponse,
// which marks the article REJECTED (plan §12).

/*
 * Not on the gateway yet (plan §12). Each is its own query, so a missing one
 * only empties its own panel:
 *
 *   enum ArticleSkipReason { NO_KEYWORD ONE_KEYWORD_IN_TEXT OUTSIDE_SECTION }
 *   extend type IngestedArticle { skipReason: ArticleSkipReason }
 *   ingestedArticles(sourceId: ID!, limit: Int): UnroutedArticlesResponse
 *     # newest first, with the routing outcome of each article
 *
 *   type NewsSourceReviewStats {
 *     exchangeId: ID!
 *     reviewed: Int          # drafts reviewed, all time
 *     recentReviewed: Int    # of the last 20
 *     recentUnchanged: Int   # of those, published without edits
 *   }
 *   union NewsSourceReviewStatsResponse = Error | NewsSourceReviewStatsList
 *   newsSourceReviewStats(sourceId: ID!): NewsSourceReviewStatsResponse
 *
 *   previewNewsSourceRouting(samples, coverage) returns the same
 *   ArticleSkipReason per sample, from passesKeywordGate and the section
 *   check in newsRouting.service.js. Until it ships, previewSample in
 *   sourceHelpers.js estimates it in the browser.
 */

export const GET_INGESTED_ARTICLES = gql`
  query IngestedArticles($sourceId: ID!, $limit: Int) {
    ingestedArticles(sourceId: $sourceId, limit: $limit) {
      ... on Error {
        message
      }
      ... on IngestedArticleList {
        data {
          id
          title
          canonicalUrl
          exchangeId
          status
          skipReason
          publishedDate
          fetchedAt
        }
      }
    }
  }
`;

export const GET_REVIEW_STATS = gql`
  query NewsSourceReviewStats($sourceId: ID!) {
    newsSourceReviewStats(sourceId: $sourceId) {
      ... on Error {
        message
      }
      ... on NewsSourceReviewStatsList {
        data {
          exchangeId
          reviewed
          recentReviewed
          recentUnchanged
        }
      }
    }
  }
`;
